/**
 * The GS1 vocabulary, defined once — the app's term popovers and the site's glossary page
 * are two renderings of this array.
 *
 * ## Why it lands here and not in `packages/ui`
 *
 * The handover states the constraint: *"The glossary is the app's term-popover content,
 * published. Same definitions, one source. Do not fork the copy."* Measured at c54:
 * `packages/ui/src/glossary/Glossary.tsx` takes each entry as a **prop**, and no shared
 * corpus of term data existed anywhere in the repo. So there is nothing to fork yet — this
 * is the chance to establish the source before the dashboard invents its own.
 *
 * It cannot live in `packages/ui`, because `apps/site` consumes that package's `tokens.css`
 * subpath and nothing else (plan §2's boundary row, enforced by the eslint block in
 * `eslint.config.js`). It can live here, because the site may import `@packagepage/shared`.
 * That is the same argument `plans.ts` made at c54 and it lands in the same place.
 *
 * The app's popovers migrate to this module opportunistically. Nothing in `apps/app` reads
 * it today, which is stated rather than implied: this is a source waiting for its second
 * consumer, not a shared module with two.
 *
 * ## Two fields, because the design asks two different questions
 *
 * `components.md`: *"First use of a GS1 term gets a dashed underline … and a popover on
 * hover/focus: the term, **one plain-English sentence**, and the CURIE or AI number in
 * mono."* The site's glossary page wants the paragraph. Those are one definition at two
 * lengths rather than two definitions, so they are two fields on one entry — {@link
 * GlossaryTerm.gloss} and {@link GlossaryTerm.paragraph} — and the paragraph may never
 * contradict the sentence above it.
 *
 * ## Every entry carries its source, and one of them says the term is ours
 *
 * The handover's rule for Learn content is *"every factual claim about GS1 needs a source;
 * where the research does not support a claim, delete the claim — do not soften it."* A
 * glossary is nothing but factual claims about GS1, so the rule binds hardest here.
 * {@link GlossaryTerm.source} is required, and `grain` carries a source that says **we
 * invented the word** — which is the one thing a reader most needs to know about it, and
 * the thing a glossary of GS1 terms is most likely to get wrong by omission.
 *
 * ## All fifteen terms `components.md` names — and how the fifteenth was lost
 *
 * `components.md` lists: GTIN, check digit, company prefix, link type, grain, qualifier,
 * X-dimension, quiet zone, error correction, GPC, CPV, AI, linkset, resolver, Digital Link
 * URI. c59 shipped fourteen and recorded, in this docblock, that **GPC appears nowhere in
 * the vault, nowhere in the specification and nowhere in this repository**.
 *
 * **Two thirds of that sentence were false, and the instrument is what made them so.** The
 * search was for the uppercase acronym, and every occurrence in this codebase is spelled in
 * the case its language uses:
 *
 *   - `App/Technical Requirements.md`:527 — `gpc_category?` in the `products` table. **That
 *     is the spec.**
 *   - `packages/db/src/schema/catalog.ts`:52 — `gpcCategory: text("gpc_category")`
 *   - `packages/db/drizzle/0009_catalog.sql`:43 — `gpc_category  text,`
 *
 * A case-sensitive grep for `GPC` returns none of them. The conclusion drawn from that
 * silence — *the product has no such concept* — was a true observation carrying a false
 * reason, which is the failure mode this project has a standing rule about.
 *
 * The half that held is the half that mattered: **there was no definition to cite.** That is
 * now fixed at the source rather than by writing one, and GPC's entry quotes the sentence
 * GS1 publishes about it, on a page read directly on 2026-08-13.
 *
 * {@link OUTSTANDING_TERMS} is empty as a result and stays exported. A short glossary that
 * names its gap is better than a complete-looking one with an unsourced entry, and the day
 * there is another gap it needs somewhere to go that is not a comment.
 */
/**
 * The four standards these definitions rest on.
 *
 * ## A label may name a release only when the URL is pinned to that release
 *
 * **This rule is the remedy for a defect the anchor ruling exposed on its first day.**
 * `URI_SYNTAX` read *"Release 1.6.0"* and pointed at GS1's **latest-version** address. On
 * 2026-08-13 that address returns *"GS1 Digital Link Standard: URI Syntax — Release 1.7.0,
 * Ratified Aug 2026"*, so the citation disagreed with its own destination. As `<code>` text
 * nobody would have noticed; as an anchor it is one click from being read as carelessness.
 *
 * A release number and a moving URL is a claim with an expiry date. Either the URL is
 * pinned, or the label does not name a release. These four take the second form, because
 * they are cited for *definitions* — what a qualifier is, what a linkset is — which the
 * standards keep saying across releases. The changelog entries in `apps/site/src/content/`
 * take the first form: each one is *about* a release, so each pins.
 *
 * **Which addresses can be pinned was measured rather than assumed.** `/standards/resolver/
 * 1.2.0/` and `/guidelines/2d-in-retail/1.1.0/` return HTML; `/standards/genspecs/26.0/`
 * returns **404**, so GenSpecs has no versioned address in that shape and its citation
 * stays on the latest one. Guessing a URL and shipping it unopened is the failure the whole
 * rule is about.
 *
 * Which release each of these currently *is* still matters, and it is recorded where a
 * reader of this repo will look for it: `README.md`'s standards baseline. The decision this
 * comment used to say was owed was taken on 2026-08-17: c77 read 1.7.0 in full and found
 * nothing in `packages/gs1`'s behaviour moves, and c77a moved the version claim to match.
 * The reporting was the point — a citation that disagrees with its destination is a finding
 * to raise, never a number to quietly adopt.
 */
const GENSPECS = {
    label: "GS1 General Specifications",
    url: "https://ref.gs1.org/standards/genspecs/",
    // 12,175,796 bytes of PDF. Measured, and the reason the marker exists at all.
    format: "pdf",
};
const URI_SYNTAX = {
    label: "GS1 Digital Link Standard: URI Syntax",
    url: "https://ref.gs1.org/standards/digital-link/uri-syntax/",
};
const RESOLVER_STANDARD = {
    label: "GS1-Conformant Resolver Standard",
    url: "https://ref.gs1.org/standards/resolver/",
};
const POS_GUIDELINE = {
    label: "2D Barcodes at Retail Point-of-Sale Implementation Guideline",
    url: "https://ref.gs1.org/guidelines/2d-in-retail/",
};
/**
 * One global namespace, said publicly — Decisions/2026-10-28 The Resolver Is One Global
 * Namespace. The domain screen states it before a hostname is added, so it is one constant.
 */
export const RESOLVER_NAMESPACE = "Ours is one namespace rather than one per customer: a brand's own resolver hostname answers any code packagepage holds, not only that brand's. Nothing lists what it holds, so a code has to be known before it can be asked for.";
/**
 * The terms, alphabetical by {@link GlossaryTerm.id}.
 *
 * Alphabetical rather than conceptual, because a glossary is looked things up in rather
 * than read through, and the order a page renders should be the order a reader scans for.
 *
 * **By `id`, not by `term`** — this docblock said `term` until c60 and the array never was:
 * `company-prefix` renders as *"GS1 Company Prefix"* and sits third, before *"Consumer
 * product variant"*. The id is the stable half (it is a locked slug), the display name is
 * not, and sorting by a name that starts *"GS1 …"* would file four unrelated terms under G.
 */
export const GLOSSARY = [
    {
        id: "application-identifier",
        term: "Application identifier (AI)",
        mono: "01 · 10 · 21",
        gloss: "The two-to-four-digit code in front of a value that says what the value means. 01 is a GTIN, 10 a batch, 21 a serial number.",
        paragraph: "GS1 data is self-describing: every value is preceded by a number that names it. That number is the application identifier. It is why a machine reading a code does not need to be told what the digits after it are, and it is why the parts of a Digital Link URI can never be reordered. The identifier and the value are one unit, and the path is a sequence of those units.",
        source: GENSPECS,
    },
    {
        id: "check-digit",
        term: "Check digit",
        gloss: "The last digit of a GTIN, computed from the digits before it, so a mistyped number can be caught before it is printed.",
        paragraph: "A check digit is arithmetic, not a lookup: multiply the other digits by an alternating weighting, sum them, and the check digit is what takes the total to the next multiple of ten. It catches every single-digit mistake and most transpositions. It is the reason an import can tell you a number is wrong without knowing anything about your products, and the reason a barcode scanner does not silently return a different product when one bar is misread.",
        source: GENSPECS,
    },
    {
        id: "company-prefix",
        term: "GS1 Company Prefix",
        gloss: "The block of digits GS1 licenses to your company. Every GTIN you issue begins with it, which is what makes your numbers yours.",
        paragraph: "You license a prefix from your local GS1 member organisation, and its length decides how many GTINs you can issue underneath it, and a shorter prefix leaves more digits for products. Prefixes are licensed rather than sold, and are renewed annually. A single GTIN can also be bought outright for one product without a prefix, which is often the right first step for a brand with one SKU.",
        source: {
            label: "GS1 US — GS1 Company Prefix",
            url: "https://store.gs1us.org/gs1-company-prefix/p",
        },
    },
    {
        id: "cpv",
        term: "Consumer product variant (CPV)",
        mono: "22",
        gloss: "A qualifier for a version of a product a shopper can tell apart but that does not warrant a different GTIN.",
        paragraph: "Some differences matter to a person holding the pack and not to a supply chain: a seasonal sleeve, a promotional flash, a recipe tweak that changes nothing on the nutrition panel. The consumer product variant is where those live. It sits between the GTIN and the batch in the fixed qualifier order, so a link defined for one variant does not disturb the links defined for the product as a whole.",
        source: URI_SYNTAX,
    },
    {
        id: "digital-link-uri",
        term: "GS1 Digital Link URI",
        mono: "/01/{gtin}/10/{lot}",
        gloss: "A web address that carries a GTIN in a structured path, so one printed code both identifies the product and points at information about it.",
        paragraph: "The structure is fixed: a domain you choose, then the primary identifier and its value, then any qualifiers in a defined order, with data attributes in the query string. Because the GTIN is in the path rather than hidden in a short link, a point-of-sale system can read the number without following the address at all, which is what lets a single symbol serve both the checkout lane and the shopper's phone.",
        source: URI_SYNTAX,
    },
    {
        id: "error-correction",
        term: "Error correction",
        gloss: "Redundant data inside a symbol that lets a scanner still read it when part of the printed code is scuffed, folded or under-inked.",
        paragraph: "QR Code offers four levels, roughly 7%, 15%, 25% and 30% of the symbol recoverable, and the level is a choice, traded against how much data fits in the same area. Data Matrix has one level, ECC200, at roughly 30%. More correction is not automatically better: it makes the symbol denser at the same physical size, and a denser symbol needs a larger X-dimension to stay readable.",
        source: GENSPECS,
    },
    {
        id: "gpc",
        term: "Global Product Classification (GPC)",
        gloss: "GS1's scheme for saying what kind of thing a product is, as distinct from which product it is.",
        paragraph: "GS1 describes it as a system that gives buyers and sellers a common language for grouping products in the same way, everywhere in the world. A GTIN identifies one product. A GPC category says what category that product belongs to, in terms a trading partner on the other side of the world uses too. It is what a retailer's system means when it asks which classification your item sits in. A product in packagepage can carry a GPC category, and nothing here requires one. It is a field you fill in when a trading partner asks you for it.",
        source: {
            label: "GS1 standards repository — Global Product Classification",
            // The page the quoted sentence is actually on, read 2026-08-13. NOT
            // `/standards/gpc/`, which is an 11,105,615-byte ZIP of the classification data and
            // contains no prose — citing it would be citing a document nobody read.
            url: "https://ref.gs1.org/standards/",
        },
    },
    {
        id: "grain",
        term: "Grain",
        mono: "gtin · batch · serial",
        gloss: "Our word, not GS1's, for the level a link is defined at: the product, one batch of it, or one individual unit.",
        paragraph: "GS1 has the qualifiers, and we needed a single noun for what they pick out, because almost everything the product does depends on it. A link defined at the product level is inherited by every batch and every unit beneath it. A link defined at the batch level shadows the product-level link of the same type, and only of the same type, which is why publishing a recall notice has to write two links rather than one. When you see 'grain' in the product, this is what it means. You will not find the word in a GS1 document.",
        // No URL, deliberately: the source is our own specification (§3.8) and `packages/gs1`'s
        // GRAINS constant, neither of which is a public document. See {@link GlossarySource}.
        source: {
            label: "packagepage — our own vocabulary, not a GS1 term",
        },
    },
    {
        id: "gtin",
        term: "GTIN",
        mono: "01",
        gloss: "The number that identifies a product. It is the thing every barcode has always carried, and the part of a 2D code that must never change.",
        paragraph: "A GTIN identifies a trade item: a specific product in a specific pack size. It is the number underneath an EAN-13 or a UPC, and it is the number inside a 2D code's web address. In a Digital Link URI it is always written as fourteen digits, with shorter forms padded on the left with zeroes, so that one length works everywhere. GTIN is a GS1 trademark and a data type. It is never a brand or a product name.",
        source: GENSPECS,
    },
    {
        id: "link-type",
        term: "Link type",
        mono: "gs1:pip",
        gloss: "The label on a link that says what is at the other end, so a machine can ask for one kind of answer specifically.",
        paragraph: "A product page, allergen information, a recall notice and an instruction manual are four different answers to the same scan, and the link type is how a request says which one it wants. GS1 publishes the vocabulary. A resolver that is asked for a type it does not have answers with a 404 rather than quietly returning something else. One type per identifier is marked as the default, the one a phone camera gets when it asks for nothing in particular.",
        source: {
            label: "GS1 Web Vocabulary — link types",
            url: "https://ref.gs1.org/voc/data/linktypes",
            // The machine-readable registry itself, not a page about it. Measured:
            // `application/json`, 13,987 bytes.
            format: "json",
        },
    },
    {
        id: "linkset",
        term: "Linkset",
        mono: "application/linkset+json",
        gloss: "The whole list of links attached to one identifier, returned as JSON so a system can read every answer at once instead of following a redirect.",
        paragraph: "A phone camera wants one destination. A retailer's system, a regulator's crawler or your own integration usually wants the map. Asking a conformant resolver for the linkset, by query parameter or by content negotiation, returns every link it holds for that identifier, each with its type and any language or media it is scoped to, in the format RFC 9264 defines.",
        source: RESOLVER_STANDARD,
    },
    {
        id: "qualifier",
        term: "Qualifier",
        mono: "22 · 10 · 21",
        gloss: "An identifier that narrows a GTIN to something more specific, a consumer variant, a batch or a single unit, in a fixed order.",
        paragraph: "The order is not a convention, it is normative: consumer product variant, then batch or lot, then serial number. A URI may stop at any point in that sequence but may not skip a step out of order. That fixed shape is what lets a resolver reason about specificity. It can tell that a request for a serial number sits underneath a batch, which sits underneath the product, without knowing anything about your catalogue.",
        source: URI_SYNTAX,
    },
    {
        id: "quiet-zone",
        term: "Quiet zone",
        gloss: "The empty margin a scanner needs around a symbol to find its edges: four modules on every side for QR Code, one for Data Matrix.",
        paragraph: "The quiet zone is part of the symbol, not the space around it, and it is the single most common reason a code that looks fine fails on press. Artwork that crowds it, a rule, a fold, the edge of the label, makes a code that reads on a desk and fails on a pallet. It is measured in modules rather than millimetres, so it scales with the X-dimension and has to be recalculated whenever the symbol is resized.",
        source: GENSPECS,
    },
    {
        id: "resolver",
        term: "Resolver",
        gloss: "The service a scanned Digital Link URI reaches, which decides which link the scanner should get and sends it there.",
        paragraph: "It is the piece that makes a printed code editable after it is printed. The code carries an address. The resolver decides what that address currently answers, and it can change that answer without anyone reprinting anything. Because packaging outlives software, a resolver's only real requirement is that it keeps answering, which is why ours holds its read model on every node and needs no database on the path a scan takes. " +
            RESOLVER_NAMESPACE,
        source: RESOLVER_STANDARD,
    },
    {
        id: "x-dimension",
        term: "X-dimension",
        gloss: "The width of one module, the smallest square in a 2D symbol, in millimetres.",
        paragraph: "It is the number that decides whether a code works at a checkout lane, and it is not a matter of taste. For retail point of sale, a 2D symbol's X-dimension must be 1.5× the X-dimension of the equivalent linear barcode. The tables that give the equivalents live in the GS1 General Specifications, and the value depends on the symbology and the application. A symbol that scans on your phone at any size can still be too small for a lane, and you will not find out until the labels are on pallets.",
        source: POS_GUIDELINE,
    },
];
/**
 * Terms `components.md` names that this module does not define, with the reason.
 *
 * A list rather than a comment, so the site's glossary page can **say what is missing**
 * rather than looking complete. A gap a reader can see is a gap somebody fixes; a gap only
 * the author knows about is one that survives to launch.
 */
export const OUTSTANDING_TERMS = [
// EMPTY, and deliberately still here. GPC was its one member from c59 until c60, when the
// definition was sourced from a GS1 page read directly — see this module's header for how
// it went missing in the first place, which is the part worth keeping.
//
// The export stays because the mechanism is the point: a gap a reader can see is a gap
// somebody fixes, and the next one needs somewhere to go that is not a comment. The
// glossary page renders this block only when the list is non-empty, so an empty list
// costs a reader nothing.
];
/** One term by its permanent id, or `undefined`. The lookup the app's popovers will use. */
export function glossaryTerm(id) {
    return GLOSSARY.find((entry) => entry.id === id);
}
//# sourceMappingURL=glossary.js.map